import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { useNavigate, Link } from "react-router-dom";
import { Loader2, Lock } from "lucide-react";
import { Logo } from "../components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GoogleMark = () => (
    <svg width="16" height="16" viewBox="0 0 48 48">
        <path fill="#FFC107" d="M43.6 20.1H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3l5.7-5.7C34.5 6.1 29.5 4 24 4 13 4 4 13 4 24s9 20 20 20 20-9 20-20c0-1.3-.1-2.6-.4-3.9z"/>
        <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3l5.7-5.7C34.5 6.1 29.5 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
        <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/>
        <path fill="#1976D2" d="M43.6 20.1H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C36.9 39.2 44 34 44 24c0-1.3-.1-2.6-.4-3.9z"/>
    </svg>
);

function formatApiErrorDetail(detail) {
    if (detail == null) return "Something went wrong. Please try again.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
}

const inputCls =
    "w-full bg-[#14110F] border border-[#2E2620] rounded-sm px-4 py-3 text-sm text-[#F4EFEA] placeholder-[#6E6156] outline-none focus:border-[#C58B49]/70 transition-colors duration-300";

export default function Login() {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        // Returning from Google OAuth is handled by AuthCallback — skip the session check
        if (window.location.hash?.includes("session_id=")) return;
        axios
            .get(`${API}/auth/me`, { withCredentials: true })
            .then(() => navigate("/admin"))
            .catch(() => {});
    }, [navigate]);

    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const googleLogin = () => {
        const redirectUrl = window.location.origin + "/admin";
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    };

    const submit = async (e) => {
        e.preventDefault();
        if (loading) return;
        setLoading(true);
        setError("");
        try {
            await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
            navigate("/admin");
        } catch (err) {
            setError(formatApiErrorDetail(err.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div data-testid="login-page" className="min-h-screen bg-[#0F0C08] grain relative flex items-center justify-center px-5">
            <div
                className="absolute inset-0 pointer-events-none"
                style={{ background: "radial-gradient(50% 40% at 50% 20%, rgba(197,139,73,0.12) 0%, rgba(197,139,73,0) 70%)" }}
            />
            <motion.div
                initial={{ opacity: 0, y: 28 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                className="relative w-full max-w-md rounded-md border border-[#2E2620] bg-[#1D1814] p-8 sm:p-10"
            >
                <div className="flex flex-col items-center mb-8">
                    <Logo className="h-14" />
                    <div className="mt-6 flex items-center gap-2 text-[#C58B49]">
                        <Lock size={13} strokeWidth={1.8} />
                        <span className="font-mono2 text-[10px] tracking-[0.3em] uppercase">Owner Access</span>
                    </div>
                    <h1 className="mt-3 font-display text-3xl font-semibold text-[#F4EFEA]">Welcome back</h1>
                </div>

                <form onSubmit={submit} data-testid="login-form">
                    <label className="block text-[11px] tracking-[0.2em] uppercase text-[#8C8075] mb-2">Email</label>
                    <input
                        data-testid="login-email-input"
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@fnbinternational.com"
                        className={inputCls}
                    />
                    <label className="block text-[11px] tracking-[0.2em] uppercase text-[#8C8075] mb-2 mt-5">Password</label>
                    <input
                        data-testid="login-password-input"
                        type="password"
                        required
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="••••••••••"
                        className={inputCls}
                    />
                    {error && (
                        <p data-testid="login-error" className="mt-4 text-sm text-[#E08080] bg-[#2A1512] border border-[#5A2A22] rounded-sm px-4 py-2.5">
                            {error}
                        </p>
                    )}
                    <button
                        data-testid="login-submit-btn"
                        type="submit"
                        disabled={loading}
                        className="mt-7 w-full px-8 py-3.5 bg-[#C58B49] text-[#0F0C08] text-[13px] font-medium tracking-[0.16em] uppercase rounded-sm hover:bg-[#D4A373] disabled:opacity-60 transition-colors duration-300 flex items-center justify-center gap-2"
                    >
                        {loading && <Loader2 size={15} className="animate-spin" />}
                        {loading ? "Signing in…" : "Sign In"}
                    </button>
                </form>

                <div className="mt-6 flex items-center gap-4">
                    <span className="flex-1 h-px bg-[#2E2620]" />
                    <span className="text-[10px] tracking-[0.25em] uppercase text-[#6E6156]">or</span>
                    <span className="flex-1 h-px bg-[#2E2620]" />
                </div>

                <button
                    data-testid="login-google-btn"
                    onClick={googleLogin}
                    className="mt-6 w-full px-8 py-3.5 border border-[#2E2620] text-[#E2D4C9] text-[13px] tracking-[0.14em] uppercase rounded-sm hover:border-[#C58B49]/60 hover:text-[#F4EFEA] transition-colors duration-300 flex items-center justify-center gap-3"
                >
                    <GoogleMark /> Continue with Google
                </button>

                <Link
                    data-testid="login-back-home"
                    to="/"
                    className="mt-7 block text-center text-xs tracking-[0.15em] uppercase text-[#8C8075] hover:text-[#D4A373] transition-colors"
                >
                    ← Back to website
                </Link>
            </motion.div>
        </div>
    );
}
